import React from 'react';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { AppButton, FlexibleSelectDropdown, SimpleModal } from '@Components/Control';
import { buildDateTimeLabel } from '@Helpers/util';
import DateFilterDropdown from '../DateFilterDropdown';
import { CLAIM_DATE_FILTER_OPTIONS } from '../constants';
import {
  TableWrapper,
  NoDataPlaceholder,
  tablePadding,
  renderTableHeader,
  SearchInput,
} from '../../SharedComponents';
import { ManualClaimStatusPill } from './manualClaimUiHelpers';
import {
  MANUAL_CLAIM_REQUEST_STATUS_OPTIONS as STATUS_OPTIONS,
  MANUAL_CLAIM_SORT_OPTIONS as SORT_OPTIONS,
  MANUAL_CLAIM_SUBMISSION_TYPE,
} from './manualClaimConstants';

const HEADER = [
  { display: 'Company', uid: 'companyId', size: '18%' },
  { display: 'Status', uid: 'status', size: '12%' },
  { display: 'Source', uid: 'submissionType', size: '14%' },
  { display: 'Items', uid: 'itemSummary', size: '22%' },
  { display: 'Submitted', uid: 'createdAt', size: '14%' },
  { display: '', uid: 'actions', size: '16%' },
];

const AdminManualClaimsRequestsTable = ({
  small,
  loading,
  data,
  handlePage,
  currentPage,
  onOpenDetail,
  query = {},
  onQueryChange,
  companyIdInput = '',
  onCompanyIdChange,
  companyOptions = [],
  companyNameById = {},
  onCompanySelect,
}) => {
  const rows = data?.requests || [];
  const pagination = data?.pagination || {};
  const { total = 0, limit = 25 } = pagination;
  const { theme: { page, button } } = useAppTheme();
  const [isBreakdownOpen, setIsBreakdownOpen] = React.useState(false);
  const [selectedSummary, setSelectedSummary] = React.useState(null);
  const [searchResetCount, setSearchResetCount] = React.useState(0);

  const hasActiveFilters = Object.values(query).some(Boolean)
    || String(companyIdInput || '').trim() !== '';

  const openBreakdown = (summary) => {
    setSelectedSummary(summary || {});
    setIsBreakdownOpen(true);
  };

  const getCompanyLabel = (row) => {
    const directName = row.companyName || row.company_name;
    if (directName) return directName;
    const id = row.companyId ?? row.company_id;
    if (typeof id === 'undefined' || id === null) return '—';
    return companyNameById[String(id)] || id;
  };

  const renderCell = (uid, row) => {
    if (uid === 'id') {
      return (
        <Text
          size="0.85rem"
          color={page.tableText}
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            wordBreak: 'break-all',
            maxWidth: '100%',
          }}
        >
          {row.id}
        </Text>
      );
    }
    if (uid === 'companyId') {
      return (
        <Text size="1rem" color={page.tableText} weight={600}>
          {getCompanyLabel(row)}
        </Text>
      );
    }
    if (uid === 'status') {
      return <ManualClaimStatusPill status={row.status} page={page} />;
    }
    if (uid === 'submissionType') {
      const label = row.submissionType === MANUAL_CLAIM_SUBMISSION_TYPE.CSV_UPLOAD
        ? 'CSV'
        : 'URL input';
      return (
        <Text size="1rem" color={page.tableText}>
          {label}
        </Text>
      );
    }
    if (uid === 'itemSummary') {
      const s = row.itemSummary || {};
      const itemTotal = s.total ?? 0;
      return (
        <Text size="1rem" color={page.tableText} style={{ lineHeight: 1.45 }}>
          <Text weight={600} as="span" color={page.tableText}>
            {itemTotal}
          </Text>
          {' total · '}
          <Text
            as="span"
            color={page.highlight}
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              openBreakdown(s);
            }}
            size="1rem"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                openBreakdown(s);
              }
            }}
          >
            Summary
          </Text>
        </Text>
      );
    }
    if (uid === 'createdAt') {
      return (
        <Text size="1rem" color={page.tableText}>
          {buildDateTimeLabel(row.createdAt)}
        </Text>
      );
    }
    if (uid === 'actions') {
      return (
        <AppButton
          plain
          level="tableControls"
          label="View details"
          color={page.highlight}
          onClick={() => onOpenDetail(row.id)}
        />
      );
    }
    return null;
  };

  const renderContents = () => {
    if (loading) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={false}
          buttonHighlight={button.primaryBg}
          loading
          label="Loading manual claim requests..."
          noDataLabel=""
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }
    if (!rows.length) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={false}
          buttonHighlight={button.primaryBg}
          loading={false}
          label={
            hasActiveFilters
              ? 'No requests match your filters.'
              : 'No manual claim requests yet.'
          }
          noDataLabel={
            hasActiveFilters
              ? 'Try clearing filters or check back when customers submit new batches.'
              : 'When customers submit batches, they will appear here.'
          }
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        {!small && (
          <Box
            fill="horizontal"
            wrap={false}
            direction="row"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={size}
                pad={{
                  horizontal: '0.5rem',
                  top: '1rem',
                  bottom: '0.5rem',
                }}
              >
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        {rows.map((row) => (
          <Box
            key={row.id || uniqid()}
            direction={small ? 'column' : 'row'}
            align={small ? 'start' : 'center'}
            border={{
              color: page.tableBorder,
              size: '1px',
              style: 'solid',
              side: 'bottom',
            }}
            pad={{ vertical: '1rem', right: '0.5rem' }}
            background={page.tableBg}
            fill="horizontal"
          >
            {HEADER.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={small ? '100%' : size}
                pad={{
                  horizontal: small ? '0' : '0.5rem',
                  vertical: small ? '0.25rem' : '0',
                }}
                direction="row"
                gap="0.5rem"
              >
                {small && (
                  <Text size="0.875rem" color={page.tableHeader}>
                    {display}
                    :
                  </Text>
                )}
                {renderCell(uid, row)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      {isBreakdownOpen && (
        <SimpleModal
          small={small}
          toggleModal={setIsBreakdownOpen}
          modalTitle="Claim status breakdown"
          textColor={page.primaryText}
          highlightColor={page.highlight}
          modalBg={page.modalBg}
          closeLabel="Close"
          desktopMinWidth="34rem"
        >
          <Box pad={{ vertical: '1rem' }} gap="0.75rem">
            <Box direction="row" justify="between" gap="1rem">
              <Text color={page.secondaryText} weight={600}>Total</Text>
              <Text color={page.primaryText} weight={600}>{selectedSummary?.total ?? 0}</Text>
            </Box>
            <Box direction="row" justify="between" gap="1rem">
              <Text color={page.secondaryText} weight={600}>Pending</Text>
              <Text color={page.primaryText} weight={600}>{selectedSummary?.pending ?? 0}</Text>
            </Box>
            <Box direction="row" justify="between" gap="1rem">
              <Text color={page.secondaryText} weight={600}>Monetized</Text>
              <Text color={page.primaryText} weight={600}>{selectedSummary?.monetized ?? 0}</Text>
            </Box>
            <Box direction="row" justify="between" gap="1rem">
              <Text color={page.secondaryText} weight={600}>Takendown</Text>
              <Text color={page.primaryText} weight={600}>{selectedSummary?.takendown ?? 0}</Text>
            </Box>
            <Box direction="row" justify="between" gap="1rem">
              <Text color={page.secondaryText} weight={600}>Rejected</Text>
              <Text color={page.primaryText} weight={600}>{selectedSummary?.rejected ?? 0}</Text>
            </Box>
          </Box>
        </SimpleModal>
      )}

      <TableWrapper
        zeroBasedPageIndex
        small={small}
        header="Manual claim requests"
        boxPad={tablePadding(small)}
        isPaged={total > limit}
        totalResults={total}
        currentResults={rows.length}
        selectedResultsCount={limit}
        currentPage={currentPage}
        handlePage={handlePage}
        overrideNextPageDisabled={
          pagination?.offset + pagination?.limit >= pagination?.total
        }
        tableControls={onQueryChange ? (
          <Box wrap direction="row" gap="0.5rem" align="center">
            <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
              <FlexibleSelectDropdown
                textColor={page.inputText}
                handleItemSelect={(value) => onQueryChange('status', value || null)}
                flexibleOptions={STATUS_OPTIONS}
                noSelectionLabel="All statuses"
                displayKey="display"
                valueKey="api"
                iconPath="images/filter-input-icon.svg"
                customPad="0.2rem 0.3rem"
                selectedOption={STATUS_OPTIONS.find((o) => o.api === query.status)?.display || 'All statuses'}
                withReset={!!query.status}
              />
            </Box>
            <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
              <DateFilterDropdown
                small={small}
                query={query}
                onDateSelect={(key, date) => onQueryChange(key, date)}
                onQueryChange={onQueryChange}
                dateFilterOptions={CLAIM_DATE_FILTER_OPTIONS}
              />
            </Box>
            <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
              <FlexibleSelectDropdown
                textColor={page.inputText}
                handleItemSelect={(value) => onQueryChange('sortOrder', value === 'desc' ? null : value)}
                flexibleOptions={SORT_OPTIONS}
                noSelectionLabel="Newest first"
                displayKey="display"
                valueKey="api"
                iconPath="images/filter-input-icon.svg"
                customPad="0.2rem 0.3rem"
                selectedOption={SORT_OPTIONS.find((o) => o.api === (query.sortOrder || 'desc'))?.display || 'Newest first'}
                withReset={query.sortOrder === 'asc'}
              />
            </Box>
            {onCompanySelect && companyOptions.length > 0 && (
              <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
                <FlexibleSelectDropdown
                  textColor={page.inputText}
                  handleItemSelect={(value) => onCompanySelect(value)}
                  flexibleOptions={companyOptions}
                  noSelectionLabel="All companies"
                  displayKey="display"
                  valueKey="api"
                  iconPath="images/filter-input-icon.svg"
                  customPad="0.2rem 0.3rem"
                  selectedOption={
                    companyOptions
                      .find((opt) => String(opt.api) === String(companyIdInput))
                      ?.display
                    || 'All companies'
                  }
                  withReset={String(companyIdInput).trim() !== ''}
                  withSearch
                />
              </Box>
            )}
            <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
              <SearchInput
                key={searchResetCount}
                customPlaceholder="Filter by URL/Channel"
                small={small}
                handleSearch={(_, value) => onQueryChange('search', value || null)}
                initialValue={query.search || ''}
              />
            </Box>
            <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
              <AppButton
                overrideHover
                disabled={!hasActiveFilters}
                onClick={() => {
                  setSearchResetCount((c) => c + 1);
                  onQueryChange('reset');
                  if (onCompanyIdChange) onCompanyIdChange('');
                }}
                level="tableControls"
                height="var(--table-controls-height)"
                color={button.primaryText}
                bgColor={button.primaryBg}
                label="Reset"
              />
            </Box>
          </Box>
        ) : null}
      >
        <Box fill="horizontal" direction="column">
          {renderContents()}
        </Box>
      </TableWrapper>
    </>
  );
};

AdminManualClaimsRequestsTable.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  data: PropTypes.shape({
    requests: PropTypes.arrayOf(PropTypes.object),
    pagination: PropTypes.object,
  }),
  handlePage: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  onOpenDetail: PropTypes.func.isRequired,
  query: PropTypes.object,
  onQueryChange: PropTypes.func,
  companyIdInput: PropTypes.string,
  onCompanyIdChange: PropTypes.func,
  companyOptions: PropTypes.arrayOf(PropTypes.shape({
    api: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    display: PropTypes.string.isRequired,
  })),
  companyNameById: PropTypes.objectOf(PropTypes.string),
  onCompanySelect: PropTypes.func,
};

export default AdminManualClaimsRequestsTable;
